// Keycloak OIDC config per ADR-007 — `operators` realm (NOT customers).
//
// Plan madde 2 hardens this with TOTP-mandatory enrollment + MFA acr
// claim verification + step-up sudo for elevated operations. Phase 1
// scaffold wires the OIDC dance only; the realm itself + first-signin
// forced enrollment + WebAuthn flag-readiness arrive in plan madde 2.1
// + 2.3 + 2.9.
//
// Cross-realm rejection: the operator console NEVER accepts a customers-
// realm token, and the backend platform-graph operator verifier (plan
// madde 2.4) NEVER accepts an operators-realm token at /v1/* customer
// routes. Realm separation is the single most important auth invariant
// for spec 60.
//
// All values overridable via VITE_OPERATOR_KEYCLOAK_* env vars so per-
// environment builds (staging, prod) point at the right authority.

import type { AuthProviderProps } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";

import { resolveOperatorKeycloakAuthority } from "@/config/operator-deployment";

export const authority =
  resolveOperatorKeycloakAuthority({
    configuredUrl: import.meta.env["VITE_OPERATOR_KEYCLOAK_AUTHORITY"],
    mode: import.meta.env.PROD ? "production" : "development",
  });
export const clientId =
  import.meta.env["VITE_OPERATOR_KEYCLOAK_CLIENT_ID"] ?? "operator-console";
// Exported so the DEV-ONLY dev-auth bootstrap can build a UserManager whose
// settings match this provider's EXACTLY — that identity is what makes the
// stored user land on the key react-oidc-context reads.
export const redirectUri =
  import.meta.env["VITE_OPERATOR_OIDC_REDIRECT_URI"] ??
  (typeof window !== "undefined" ? `${window.location.origin}/callback` : "");
const postLogoutRedirectUri =
  import.meta.env["VITE_OPERATOR_OIDC_POST_LOGOUT_URI"] ??
  (typeof window !== "undefined" ? window.location.origin : "");

// Keep tokens out of localStorage (XSS exposure); sessionStorage scopes them to
// the tab and clears on close. Hoisted out of the config literal so dev-auth
// reuses the SAME store instance rather than constructing a second one.
export const userStore =
  typeof window !== "undefined"
    ? new WebStorageStateStore({ store: window.sessionStorage })
    : undefined;

export const oidcConfig: AuthProviderProps = {
  authority,
  client_id: clientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: postLogoutRedirectUri,
  response_type: "code",
  // openid + profile + email are the spec 60 baseline. acr_values
  // (mfa, mfa+webauthn) wire in plan madde 2.7 once the step-up
  // hook is in place.
  scope: "openid profile email",
  loadUserInfo: true,
  automaticSilentRenew: true,
  // Operator console runs a shorter idle timeout than customer-web (plan
  // madde 2.8).
  userStore,
  onSigninCallback: (): void => {
    // Strip the OIDC params from the URL after the redirect lands so
    // the operator never sees ?code=... in their address bar.
    if (typeof window !== "undefined") {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  },
};
